import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { AvatarModule } from 'primeng/avatar';
import { RatingModule } from 'primeng/rating';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { SkeletonModule } from 'primeng/skeleton';
import { PipesModule } from '../../pipes/pipes.module';
import { ClientPortalService } from '../../services/client-portal.service';
import { ConfigurationService } from '../../services/configuration.service';
import { ClientPortalReviewerProfile } from '../../interfaces/client-portal.interface';

/**
 * MP-5754: the redacted reviewer profile a CLIENT opens from Worked Together.
 *
 * A slim read-only card, not the shared ReviewerProfileComponent: that one
 * hard-wires email, phone, address, pay rates, favorites, documents and
 * notes into its template and fires six firm endpoints on init, all 403 for
 * a CLIENT. This card renders only what `client-portal/reviewers/:id`
 * returns: name, rating, ranking, the group's projects and the position
 * history on them. No contact channel exists in the payload or the template.
 */
@Component({
  selector: 'app-client-reviewer-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, AvatarModule, RatingModule, TableModule, TagModule, ChipModule, SkeletonModule, PipesModule],
  templateUrl: './client-reviewer-profile.component.html',
  styleUrl: './client-reviewer-profile.component.scss'
})
export class ClientReviewerProfileComponent implements OnInit {
  readonly profile = signal<ClientPortalReviewerProfile | null>(null);
  readonly isLoading = signal(true);
  /** Set on a failed load (404: the reviewer never worked on the group's projects). */
  readonly notFound = signal(false);
  readonly isPwcEnabled = signal(false);
  readonly displayName = computed(() => {
    const profile = this.profile();
    return profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : '';
  });
  readonly projectNames = computed(() =>
    (this.profile()?.projects || []).map((project) => project.codeName).filter((name): name is string => !!name));

  constructor(public config: DynamicDialogConfig, private clientPortalService: ClientPortalService,
    private configurationService: ConfigurationService) { }

  ngOnInit(): void {
    this.isPwcEnabled.set(this.configurationService.getPwcFlag());
    this.getProfile();
  }

  getProfile(): void {
    const reviewerId = this.config.data?.reviewerId;
    if (!reviewerId) {
      this.isLoading.set(false);
      this.notFound.set(true);
      return;
    }
    this.clientPortalService.getReviewer(reviewerId).subscribe({
      next: (profile: ClientPortalReviewerProfile) => {
        this.profile.set(profile);
        this.isLoading.set(false);
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      }
    });
  }
}
